// src/components/AuthGuard.jsx
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRights } from '../contexts/RightsContext';

const AuthGuard = ({ module, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { rights, loading, hasRight, getFirstAllowedModule } = useRights();

  // ============================================================================
  // Redirection via useEffect
  // ============================================================================
  useEffect(() => {
    if (loading || !rights) return;

    // Route "/" sans module → redirection vers premier module autorisé
    if (!module) {
      const firstModule = getFirstAllowedModule();
      if (firstModule) {
        console.log('🔀 [AuthGuard] Redirection "/" →', firstModule);
        navigate(firstModule, { replace: true });
      }
      return;
    }

    // Admin a tous les droits → pas de redirection
    if (user?.role === 'admin') return;

    // Module spécifique non autorisé → redirection
    if (!hasRight(module)) {
      const firstModule = getFirstAllowedModule();
      if (firstModule) {
        console.log(`🔀 [AuthGuard] Module "${module}" interdit → redirection vers`, firstModule);
        navigate(firstModule, { replace: true });
      }
    }
  }, [loading, rights, module, user, hasRight, getFirstAllowedModule, navigate]);

  // ============================================================================
  // Render
  // ============================================================================

  // Pendant le chargement
  if (loading || !rights) {
    return null;
  }

  // Route "/" sans module → null pendant que useEffect redirige
  if (!module) {
    return null;
  }

  // Admin a tous les droits
  if (user?.role === 'admin') {
    return children || null;
  }

  // Module autorisé
  if (hasRight(module)) {
    return children || null;
  }

  // Module non autorisé — useEffect va rediriger, afficher null en attendant
  return null;
};

export default AuthGuard;